import { useState, useEffect, useRef } from 'react';
import type { WardDetail as WardDetailType } from '../App';
import './WardDetail.css';

interface WardDetailProps {
  wardId: string;
  apiBase: string;
  onBack: () => void;
}

function WardDetail({ wardId, apiBase, onBack }: WardDetailProps) {
  const [ward, setWard] = useState<WardDetailType | null>(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  
  // Campaign inputs (local state)
  const [voteTarget, setVoteTarget] = useState(0.20);
  const [candidateStrength, setCandidateStrength] = useState(0.7);
  const [doorKnocks, setDoorKnocks] = useState(250);
  const [deliveries, setDeliveries] = useState(2);
  const [phonebankHours, setPhonebankHours] = useState(10);

  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    fetchWard();
  }, [wardId]);

  useEffect(() => {
    if (ward) {
      // Sync local inputs with fetched ward data
      setVoteTarget(ward.vote_target ?? 0.2);
      setCandidateStrength(ward.candidate_strength ?? 0.5);
      setDoorKnocks(ward.door_knocks_per_week ?? 0);
      setDeliveries(ward.deliveries_per_week ?? 0);
      setPhonebankHours(ward.phonebank_hours_per_week ?? 0);
      
      // Draw chart
      drawChart();
    }
  }, [ward]);

  const fetchWard = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/wards/${wardId}`);
      const data = await res.json();
      setWard(data);
    } catch (err) {
      console.error('Failed to fetch ward:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    setUpdating(true);
    try {
      // Update campaign inputs
      await fetch(`${apiBase}/wards/${wardId}/campaign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          vote_target: voteTarget,
          candidate_strength: candidateStrength,
          door_knocks_per_week: doorKnocks,
          deliveries_per_week: deliveries,
          phonebank_hours_per_week: phonebankHours,
        }),
      });

      // Recompute model
      await fetch(`${apiBase}/model/compute/${wardId}`, {
        method: 'POST',
      });

      // Refresh ward data
      await fetchWard();
    } catch (err) {
      console.error('Failed to update ward:', err);
    } finally {
      setUpdating(false);
    }
  };

  const drawChart = () => {
    if (!canvasRef.current || !ward) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);

    const w = rect.width;
    const h = rect.height;
    const padding = 50;

    ctx.clearRect(0, 0, w, h);

    // Axes
    ctx.beginPath();
    ctx.strokeStyle = '#cbd5e1';
    ctx.lineWidth = 2;
    ctx.moveTo(padding, h - padding);
    ctx.lineTo(w - padding, h - padding);
    ctx.moveTo(padding, h - padding);
    ctx.lineTo(padding, padding);
    ctx.stroke();

    // Labels
    ctx.fillStyle = '#64748b';
    ctx.font = '12px sans-serif';
    ctx.fillText('Resources (X)', w - 120, h - 20);
    ctx.save();
    ctx.translate(20, h / 2);
    ctx.rotate(-Math.PI / 2);
    ctx.fillText('Vote Share %', 0, 0);
    ctx.restore();

    // Draw saturation curve
    const K = ward.saturation_K;
    const maxShare = ward.saturation_max_share;
    const maxX = 2000;

    ctx.beginPath();
    ctx.strokeStyle = '#003366';
    ctx.lineWidth = 3;

    for (let x = 0; x <= maxX; x += 10) {
      const saturation = x / (x + K);
      const voteShare = maxShare * saturation;
      const plotX = padding + (x / maxX) * (w - 2 * padding);
      const plotY = h - padding - (voteShare / 50) * (h - 2 * padding);

      if (x === 0) {
        ctx.moveTo(plotX, plotY);
      } else {
        ctx.lineTo(plotX, plotY);
      }
    }
    ctx.stroke();

    // Current point
    const currentX = ward.resources_required_X - ward.resources_delta_X;
    const currentShare = ward.green_vote_predicted_current;
    const currentPlotX = padding + (currentX / maxX) * (w - 2 * padding);
    const currentPlotY = h - padding - (currentShare / 50) * (h - 2 * padding);

    ctx.beginPath();
    ctx.fillStyle = '#4da6ff';
    ctx.arc(currentPlotX, currentPlotY, 6, 0, 2 * Math.PI);
    ctx.fill();

    // Target line
    const targetShare = voteTarget * 100;
    const targetPlotY = h - padding - (targetShare / 50) * (h - 2 * padding);

    ctx.beginPath();
    ctx.strokeStyle = '#15803d';
    ctx.setLineDash([5, 5]);
    ctx.lineWidth = 2;
    ctx.moveTo(padding, targetPlotY);
    ctx.lineTo(w - padding, targetPlotY);
    ctx.stroke();
    ctx.setLineDash([]);

    ctx.fillStyle = '#15803d';
    ctx.font = 'bold 11px sans-serif';
    ctx.fillText(`Target: ${targetShare.toFixed(1)}%`, w - 100, targetPlotY - 8);
  };

  if (loading) {
    return (
      <div className="ward-detail-loading">
        <div className="spinner"></div>
        <p>Loading ward details...</p>
      </div>
    );
  }

  if (!ward) {
    return (
      <div className="ward-detail-error">
        <p>Ward not found</p>
        <button onClick={onBack}>← Back to Dashboard</button>
      </div>
    );
  }

  const getStatusClass = (status: string) => {
    switch (status) {
      case 'priority': return 'status-priority';
      case 'hold': return 'status-hold';
      case 'low_roi': return 'status-low';
      case 'defensive': return 'status-defensive';
      default: return '';
    }
  };

  return (
    <div className="ward-detail">
      {/* Header */}
      <div className="ward-header">
        <button className="btn-back" onClick={onBack}>
          ← Back
        </button>
        <div className="ward-title">
          <h2>{ward.name}</h2>
          <p>
            {ward.council_name} • {ward.seats} seats • {ward.green_incumbents ?? 0} Green
          </p>
        </div>
        <span className={`status-badge ${getStatusClass(ward.status ?? '')}`}>
          {ward.status || '—'}
        </span>
      </div>

      {/* Two-column layout */}
      <div className="ward-content">
        {/* Left: Campaign Inputs */}
        <div className="ward-inputs">
          <h3>Campaign Inputs</h3>
          
          <div className="input-group">
            <label>
              Vote Target: <strong>{(voteTarget * 100).toFixed(1)}%</strong>
            </label>
            <input
              type="range"
              min="0"
              max="60"
              step="1"
              value={voteTarget * 100}
              onChange={(e) => setVoteTarget(parseFloat(e.target.value) / 100)}
            />
          </div>

          <div className="input-group">
            <label>
              Candidate Strength: <strong>{candidateStrength.toFixed(2)}</strong>
            </label>
            <input
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={candidateStrength}
              onChange={(e) => setCandidateStrength(parseFloat(e.target.value))}
            />
          </div>

          <div className="input-group">
            <label>
              Door-knocks / week: <strong>{doorKnocks}</strong>
            </label>
            <input
              type="range"
              min="0"
              max="1000"
              step="10"
              value={doorKnocks}
              onChange={(e) => setDoorKnocks(parseInt(e.target.value))}
            />
          </div>

          <div className="input-group">
            <label>
              Deliveries / week: <strong>{deliveries}</strong>
            </label>
            <input
              type="range"
              min="0"
              max="10"
              step="1"
              value={deliveries}
              onChange={(e) => setDeliveries(parseInt(e.target.value))}
            />
          </div>

          <div className="input-group">
            <label>
              Phone-bank hours: <strong>{phonebankHours}</strong>
            </label>
            <input
              type="range"
              min="0"
              max="100"
              step="5"
              value={phonebankHours}
              onChange={(e) => setPhonebankHours(parseInt(e.target.value))}
            />
          </div>

          <button
            className="btn-update"
            onClick={handleUpdate}
            disabled={updating}
          >
            {updating ? 'Updating...' : 'Recalculate Model'}
          </button>

          {/* Current Metrics */}
          <div className="metrics-summary">
            <h4>Current Metrics</h4>
            <div className="metric-row">
              <span>Green Vote 2024:</span>
              <strong>{ward.green_vote_2024 ? (ward.green_vote_2024 * 100).toFixed(1) + '%' : '—'}</strong>
            </div>
            <div className="metric-row">
              <span>Predicted Current:</span>
              <strong>{ward.green_vote_predicted_current ? ward.green_vote_predicted_current.toFixed(1) + '%' : '—'}</strong>
            </div>
            <div className="metric-row">
              <span>FRAI (Agreement):</span>
              <strong>{ward.green_agreement_current ? ward.green_agreement_current.toFixed(3) : '—'}</strong>
            </div>
            <div className="metric-row">
              <span>Sweet-spot Score:</span>
              <strong>{ward.sweet_spot_score ? ward.sweet_spot_score.toFixed(3) : '—'}</strong>
            </div>
            <div className="metric-row">
              <span>Marginal Gain:</span>
              <strong>{(ward.marginal_gain * 1000).toFixed(2)}×10⁻³</strong>
            </div>
          </div>
        </div>

        {/* Right: USL Chart */}
        <div className="ward-chart">
          <h3>USL Saturation Curve</h3>
          <div className="chart-container">
            <canvas ref={canvasRef} style={{ width: '100%', height: '350px' }}></canvas>
          </div>
          
          <div className="chart-explanation">
            <h4>Model Parameters</h4>
            <div className="param-grid">
              <div className="param-item">
                <span>Drift (D):</span>
                <strong>{ward.drift_D.toFixed(3)}</strong>
              </div>
              <div className="param-item">
                <span>Dimension:</span>
                <strong>{ward.effective_dimension.toFixed(1)}</strong>
              </div>
              <div className="param-item">
                <span>Saturation K:</span>
                <strong>{ward.saturation_K.toFixed(0)}</strong>
              </div>
              <div className="param-item">
                <span>Max Share:</span>
                <strong>{ward.saturation_max_share.toFixed(0)}%</strong>
              </div>
            </div>
            
            <p className="interpretation">
              At current resources (<strong>X = {(ward.resources_required_X - ward.resources_delta_X).toFixed(0)}</strong>),
              ward saturates at <strong>{ward.green_vote_predicted_current.toFixed(1)}%</strong>.
              To hit target of <strong>{(voteTarget * 100).toFixed(1)}%</strong>, need
              <strong> ΔX = {ward.resources_delta_X.toFixed(0)}</strong> more resources.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default WardDetail;
